import { parseAlgorithm, type HashAlgorithm } from "./totp.ts";

/** Hard caps to limit resource use from oversized paste/URL input. */
export const MAX_SECRET_CHARS = 128;
export const MAX_ISSUER_CHARS = 64;
export const MAX_ACCOUNT_CHARS = 128;

/** Enough for any `digits` / `period` value; the caller checks them against the UI. */
const MAX_OPTION_CHARS = 16;

export type InitialUrlSettings = {
  secret: string | null;
  issuer: string | null;
  account: string | null;
  /** Unvalidated; the caller accepts it only if the matching `<select>` offers it. */
  digits: string | null;
  /** Unvalidated; the caller accepts it only if the matching `<select>` offers it. */
  period: string | null;
  algorithm: HashAlgorithm | null;
};

/** Safe decode: malformed percent sequences must not crash startup. */
function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function truncateInput(value: string, max: number): string {
  return value.length <= max ? value : value.slice(0, max);
}

/**
 * Accept `value` only if it is one of `allowed`. Callers pass the values a
 * `<select>` actually offers, so the markup stays the only list of legal choices.
 */
export function pickOption(
  allowed: readonly string[],
  value: string | null,
): string | null {
  return value !== null && allowed.includes(value) ? value : null;
}

/**
 * Parse one-shot initial settings. Secrets are accepted only from the fragment;
 * a `key` query parameter is deliberately never inspected.
 *
 * Values are bounded here but not checked against the set of legal choices — the
 * `<option>` lists in the markup are the single source of truth for that, so the
 * caller filters through them (see `applyToSelect` in `main.ts`).
 */
export function parseInitialUrlSettings(
  search: string,
  hash: string,
): InitialUrlSettings {
  // Slice before decoding so a malicious fragment cannot force unbounded work.
  // A Base32 character may occupy three URL-encoded characters (`%XX`).
  const rawHash = hash
    .slice(0, MAX_SECRET_CHARS * 3 + 2)
    .replace(/^#\/?/, "")
    .trim();
  const secret = rawHash
    ? truncateInput(safeDecodeURIComponent(rawHash), MAX_SECRET_CHARS)
    : null;

  const params = new URLSearchParams(search);
  const bounded = (name: string, max: number): string | null => {
    const value = params.get(name);
    return value ? truncateInput(value, max) : null;
  };

  let algorithm: HashAlgorithm | null = null;
  const rawAlgorithm = bounded("algorithm", MAX_OPTION_CHARS);
  if (rawAlgorithm) {
    try {
      algorithm = parseAlgorithm(rawAlgorithm);
    } catch {
      // Invalid algorithms are ignored.
    }
  }

  return {
    secret,
    issuer: bounded("issuer", MAX_ISSUER_CHARS),
    account: bounded("account", MAX_ACCOUNT_CHARS) ?? bounded("label", MAX_ACCOUNT_CHARS),
    digits: bounded("digits", MAX_OPTION_CHARS),
    period: bounded("period", MAX_OPTION_CHARS),
    algorithm,
  };
}
